import { useEffect, useState } from 'react';
import { useBacktest, useBacktestSnapshot, useCreateTradingDeployment, useStrategies, useTradingGateway } from '@/api/queries';
import { strategies } from '@/api/strategies';
import type { TradingGatewayDto } from '@/api/trading';
import { isCertifiedRun } from '@/components/backtests/backtestPresentation';
import { Alert } from '@/components/ui/Alert';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EnvironmentBadge } from './TradingShared';
import { routes } from '@/lib/routePaths';
import styles from '@/pages/TradingPage.module.css';

interface DeploymentWizardProps {
  accounts: readonly TradingGatewayDto[];
  backtestId?: string;
  onClose: () => void;
  onCreated: (deploymentId: string) => void;
}

export function DeploymentWizard({ accounts, backtestId, onClose, onCreated }: DeploymentWizardProps) {
  const sourced = !!backtestId;
  const sourceRun = useBacktest(backtestId ?? '', { enabled: sourced, retry: false });
  const sourceSnapshot = useBacktestSnapshot(backtestId ?? '', { enabled: sourced, retry: false });
  const strategyQuery = useStrategies({ enabled: !sourced });
  const createDeployment = useCreateTradingDeployment();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [strategyId, setStrategyId] = useState('');
  const [symbols, setSymbols] = useState<string[]>([]);
  const [warmupDays, setWarmupDays] = useState(30);
  const [maxPositionsPerDirection, setMaxPositionsPerDirection] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const selectedGateway = useTradingGateway(gatewayId);
  const sourceName = sourceRun.data?.strategyName ?? sourceSnapshot.data?.definition?.name ?? 'Frozen strategy';

  useEffect(() => {
    if (!sourced || (!sourceRun.data && !sourceSnapshot.data) || !sourceName || name) return;
    setName(safeDeploymentName(`${sourceName} deployment`));
  }, [name, sourceName, sourced]);

  const sourceFailure = sourced ? sourceEligibilityError(sourceRun.data, sourceRun.error, sourceSnapshot.error, sourceSnapshot.data) : null;
  const sourceLoading = sourced && (sourceRun.isLoading || sourceSnapshot.isLoading);

  async function create() {
    setSubmitError(null);
    try {
      const strategyDocument = sourced
        ? sourceSnapshot.data
        : (await strategies.getResource(strategyId)).document;
      if (!strategyDocument) throw new Error('The frozen strategy snapshot is unavailable.');
      const item = await createDeployment.mutateAsync({ name: name.trim(), gatewayId, strategyDocument, symbols, warmupDays, maxPositionsPerDirection });
      onCreated(item.id);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not create the deployment.');
    }
  }

  if (sourceLoading) {
    return <Dialog title="Preparing certified deployment" onClose={onClose} actions={<Button variant="ghost" onClick={onClose}>Cancel</Button>}>
      <p className="statusText statusTextMuted">Loading the run and its frozen strategy snapshot…</p>
    </Dialog>;
  }

  if (sourceFailure) {
    return <Dialog title="Deployment source unavailable" onClose={onClose} actions={<>
      <Button variant="ghost" onClick={() => void Promise.all([sourceRun.refetch(), sourceSnapshot.refetch()])}>Retry</Button>
      {backtestId && <ButtonLink variant="outline-info" to={routes.backtest(backtestId)} onClick={onClose}>Back to backtest</ButtonLink>}
    </>}>
      <Alert variant="danger"><strong>This run cannot create a deployment.</strong> {sourceFailure}</Alert>
      <p>The wizard will not substitute a newer saved strategy because deployment evidence must match the exact certified snapshot.</p>
    </Dialog>;
  }

  return <Dialog title={`New deployment · ${step} of 3`} size="wide" onClose={onClose} actions={<>
    <Button variant="ghost" onClick={() => step === 1 ? onClose() : setStep(step - 1)}>{step === 1 ? 'Cancel' : 'Back'}</Button>
    {step < 3 ? <Button disabled={(step === 1 && (!name.trim() || !gatewayId)) || (step === 2 && ((!sourced && !strategyId) || symbols.length === 0))} onClick={() => setStep(step + 1)}>Continue</Button>
      : <Button disabled={createDeployment.isPending || !!sourceFailure} onClick={() => void create()}>Create inactive deployment</Button>}
  </>}>
    {submitError && <Alert variant="danger">{submitError}</Alert>}
    {step === 1 && <div className={styles.form}>
      <Input autoFocus label="Deployment name" value={name} onChange={(event) => setName(event.target.value)} maxLength={128} />
      <Select label="Environment and account" value={gatewayId} onChange={(event) => { setGatewayId(event.target.value); setSymbols([]); }} options={[
        { value: '', label: 'Choose an account' },
        ...accounts.filter((gateway) => gateway.enabled).map((gateway) => ({ value: gateway.id, label: `${gateway.name} · ${gateway.expectedEnvironment ?? gateway.accountEnvironment ?? 'Unknown'}` })),
      ]} />
    </div>}
    {step === 2 && <div className={styles.form}>
      {sourced ? <>
        <Input label="Frozen strategy" value={sourceName} disabled readOnly />
        <Alert variant="info">Run {shortId(backtestId)} is certified. This deployment will submit its exact frozen strategy version.</Alert>
      </> : <Select label="Saved strategy" value={strategyId} onChange={(event) => setStrategyId(event.target.value)} options={[
        { value: '', label: 'Choose a strategy' },
        ...(strategyQuery.data ?? []).map((strategy) => ({ value: strategy.id, label: strategy.name })),
      ]} />}
      <Input label="Warm-up days" type="number" min={0} max={3650} value={warmupDays} onChange={(event) => setWarmupDays(Number(event.target.value))} />
      <Input label="Max positions per direction" type="number" min={1} max={10} value={maxPositionsPerDirection} onChange={(event) => setMaxPositionsPerDirection(Math.min(10, Math.max(1, Math.trunc(Number(event.target.value)) || 1)))} />
      <fieldset className={styles.fieldset}><legend>Exact broker symbols</legend>
        {selectedGateway.data?.symbols.map((symbol) => <label key={symbol.symbol}><input type="checkbox" checked={symbols.includes(symbol.symbol)} onChange={(event) => setSymbols((current) => event.target.checked ? [...current, symbol.symbol] : current.filter((value) => value !== symbol.symbol))} /> {symbol.symbol}</label>)}
        {gatewayId && selectedGateway.data?.symbols.length === 0 && <span>Connect the gateway to load its symbol catalog.</span>}
      </fieldset>
    </div>}
    {step === 3 && <div className={styles.review}>
      <EnvironmentBadge environment={selectedGateway.data?.expectedEnvironment ?? selectedGateway.data?.accountEnvironment} />
      <h3>{name}</h3>
      <dl>
        <dt>Account</dt><dd>{selectedGateway.data?.name} · {selectedGateway.data?.expectedAccountId ?? selectedGateway.data?.accountId ?? 'Awaiting identity'}</dd>
        <dt>Strategy</dt><dd>{sourced ? `${sourceName} · frozen run ${shortId(backtestId)}` : strategyQuery.data?.find((strategy) => strategy.id === strategyId)?.name}</dd>
        <dt>Symbols</dt><dd>{symbols.join(', ')}</dd>
        <dt>Max positions per direction</dt><dd>{maxPositionsPerDirection}</dd>
        <dt>Risk profile</dt><dd>{selectedGateway.data?.riskProfile ? 'Configured' : (selectedGateway.data?.expectedEnvironment ?? selectedGateway.data?.accountEnvironment) === 'Real' ? 'Required · Missing' : 'Optional · Not configured'}</dd>
        <dt>Evidence</dt><dd>{sourced ? 'The source run will appear as certified evidence when its snapshot hash matches preflight.' : 'Matching certified backtests and Paper/Demo history will be shown during authoritative preflight.'}</dd>
      </dl>
      <Alert variant="info">This creates an inactive deployment. Starting is a separate, deliberate action on its detail page.</Alert>
    </div>}
  </Dialog>;
}

export function safeDeploymentName(value: string) {
  return Array.from(value).slice(0, 128).join('');
}

function sourceEligibilityError(
  run: ReturnType<typeof useBacktest>['data'],
  runError: Error | null,
  snapshotError: Error | null,
  snapshot: ReturnType<typeof useBacktestSnapshot>['data'],
) {
  if (runError) return `The source run could not be loaded: ${runError.message}`;
  if (!run) return 'The source run is unavailable.';
  if (run.status !== 'Succeeded') return `The source run is ${run.status?.toLowerCase() ?? 'not successful'}; only successful runs are eligible.`;
  if (!isCertifiedRun(run)) return 'The source run is not certified for broker-parity execution.';
  if (snapshotError) return `The frozen strategy snapshot could not be loaded: ${snapshotError.message}`;
  if (!snapshot) return 'The frozen strategy snapshot is unavailable.';
  return null;
}

function shortId(value?: string) {
  return value ? value.slice(0, 8) : 'unknown';
}
